import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DescriptionIcon from '@mui/icons-material/Description';
import { Mail, MessageSquare, Newspaper, Globe, Grid } from 'lucide-react';

interface Section {
  id: string;
  name: string;
  selected: boolean;
}

interface Template {
  id: string;
  name: string;
  selected: boolean;
  sections: Section[];
}

interface Channel {
  id: string;
  name: string;
  icon: React.ElementType;
  expanded: boolean;
  templates: Template[];
}

interface CloneDataScenariosProps {
  open: boolean;
  onClose: () => void;
  scenarioName: string;
  onCopy?: (selectedTargets: string[]) => void;
}

// Mock data structure: EMAIL -> Template Level -> Sections
const mockChannels: Channel[] = [
  {
    id: 'email',
    name: 'Email',
    icon: Mail,
    expanded: true,
    templates: [
      {
        id: 'email-template-1',
        name: '(EL) Monthly Summary (Template-level)',
        selected: true,
        sections: [
          { id: 'section-1', name: 'Header', selected: false },
          { id: 'section-2', name: 'Welcome', selected: false },
          { id: 'section-3', name: 'Personalized Greeting', selected: false },
          { id: 'section-4', name: 'Bill Summary', selected: false },
          { id: 'section-5', name: 'Environment Impact', selected: false },
          { id: 'section-6', name: 'Cumulative Balance', selected: false },
          { id: 'section-7', name: 'Itemization', selected: false },
          { id: 'section-8', name: 'Energy Flow', selected: false },
          { id: 'section-9', name: 'TOU Rate Promotion', selected: false },
          { id: 'section-10', name: 'TOU Usage & Cost', selected: false },
          { id: 'section-11', name: 'TOU Bill Saving Insight', selected: false },
          { id: 'section-12', name: 'TOU Data Structure', selected: false }
        ]
      },
      {
        id: 'email-template-2',
        name: '(EL) Bill Projection (Template-level)',
        selected: false,
        sections: []
      },
      {
        id: 'email-template-3',
        name: '(EL) Budget Alert (Template-level)',
        selected: false,
        sections: []
      },
      {
        id: 'email-template-4',
        name: '(EL) Neighbourhood Comparison (Template-level)',
        selected: false,
        sections: []
      },
      {
        id: 'email-template-5',
        name: '(EL) EV Monthly Tracker (Template-level)',
        selected: false,
        sections: []
      }
    ]
  },
  {
    id: 'sms',
    name: 'SMS',
    icon: MessageSquare,
    expanded: false,
    templates: [
      {
        id: 'sms-template-1',
        name: '(EL) High Bill Alert SMS (Template-level)',
        selected: false,
        sections: []
      }
    ]
  },
  {
    id: 'her',
    name: 'HER',
    icon: Newspaper,
    expanded: false,
    templates: [
      {
        id: 'her-template-1',
        name: '(EL) Home Energy Report (Template-level)',
        selected: false,
        sections: []
      }
    ]
  },
  {
    id: 'web',
    name: 'Web',
    icon: Globe,
    expanded: false,
    templates: [
      {
        id: 'web-template-1',
        name: '(EL) Home Dashboard (Template-level)',
        selected: false,
        sections: []
      },
      {
        id: 'web-template-2',
        name: '(EL) Energy Details (Template-level)',
        selected: false,
        sections: []
      }
    ]
  }
];

export const CloneDataScenarios: React.FC<CloneDataScenariosProps> = ({
  open,
  onClose,
  scenarioName,
  onCopy
}) => {
  const theme = useTheme();
  const [channels, setChannels] = useState<Channel[]>(mockChannels);

  const handleToggleChannel = (channelId: string) => {
    setChannels(prevChannels => 
      prevChannels.map(ch => 
        ch.id === channelId ? { ...ch, expanded: !ch.expanded } : ch
      )
    );
  };

  const handleToggleTemplate = (channelId: string, templateId: string) => {
    setChannels(prevChannels => 
      prevChannels.map(ch => 
        ch.id === channelId 
          ? {
              ...ch,
              templates: ch.templates.map(t => 
                t.id === templateId ? { ...t, selected: !t.selected } : t
              )
            }
          : ch
      )
    );
  };

  const handleToggleSection = (channelId: string, templateId: string, sectionId: string) => {
    setChannels(prevChannels => 
      prevChannels.map(ch => 
        ch.id === channelId 
          ? {
              ...ch,
              templates: ch.templates.map(t => 
                t.id === templateId 
                  ? {
                      ...t,
                      sections: t.sections.map(s => 
                        s.id === sectionId ? { ...s, selected: !s.selected } : s
                      )
                    }
                  : t
              )
            }
          : ch
      )
    );
  };

  const getSelectedCount = (): number => {
    let count = 0;
    channels.forEach(channel => {
      channel.templates.forEach(template => {
        if (template.selected) {
          count++;
        }
        template.sections.forEach(section => {
          if (section.selected) {
            count++;
          }
        });
      });
    });
    return count;
  };

  const handleCopy = () => {
    const selectedTargets: string[] = [];
    channels.forEach(channel => {
      channel.templates.forEach(template => {
        if (template.selected) {
          selectedTargets.push(`${channel.name}:${template.name}`);
        }
        template.sections.forEach(section => {
          if (section.selected) {
            selectedTargets.push(`${channel.name}:${template.name}:${section.name}`);
          }
        });
      });
    });
    onCopy?.(selectedTargets);
    onClose();
  };

  const selectedCount = getSelectedCount();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
          maxWidth: '600px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ fontSize: 20, color: theme.palette.text.primary }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Copy Scenario: '{scenarioName}'
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'left' }}>
          Select the templates and elements where you want to copy this scenario.
        </Typography>

        <Box>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
            Select Targets:
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {channels.map((channel) => {
              const ChannelIcon = channel.icon;
              return (
                <Accordion
                  key={channel.id}
                  expanded={channel.expanded}
                  onChange={() => handleToggleChannel(channel.id)}
                  sx={{
                    '&:before': { display: 'none' },
                    boxShadow: 1,
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      channel.expanded ? (
                        <ExpandLessIcon sx={{ color: theme.palette.text.secondary }} />
                      ) : (
                        <ExpandMoreIcon sx={{ color: theme.palette.text.secondary }} />
                      )
                    }
                    sx={{
                      minHeight: 48,
                      '&.Mui-expanded': { minHeight: 48 }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                      <ChannelIcon size={18} color={theme.palette.text.primary} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {channel.name}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1, pb: 1, px: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {channel.templates.map((template) => (
                        <Box key={template.id} sx={{ pl: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={template.selected}
                                onChange={() => handleToggleTemplate(channel.id, template.id)}
                                size="small"
                                sx={{
                                  '& .MuiSvgIcon-root': {
                                    borderRadius: '4px'
                                  }
                                }}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DescriptionIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                  {template.name}
                                </Typography>
                              </Box>
                            }
                            sx={{ m: 0 }}
                          />
                          {template.sections.length > 0 && (
                            <Box sx={{ pl: 4, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              {template.sections.map((section) => (
                                <FormControlLabel
                                  key={section.id}
                                  control={
                                    <Checkbox
                                      checked={section.selected}
                                      onChange={() => handleToggleSection(channel.id, template.id, section.id)}
                                      size="small"
                                      sx={{
                                        '& .MuiSvgIcon-root': {
                                          borderRadius: '4px'
                                        }
                                      }}
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Grid size={16} color={theme.palette.text.secondary} />
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {section.name}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ m: 0 }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ 
            textTransform: 'none'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          disabled={selectedCount === 0}
          sx={{ 
            textTransform: 'none'
          }}
        >
          Copy to {selectedCount} Target{selectedCount !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

